import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchAdminContent, loginAdmin, saveAdminContent } from "@/lib/contentApi";
import { CONTENT_VERSION, getEditableSeedContent, normalizeEditableContent } from "@/data/trinityI18n";
import { AdminField, AdminListField, AdminSection } from "@/components/admin/AdminFormControls";
import { getStoredLanguage, isSupportedLanguage, storeLanguage } from "@/lib/languageStorage";
import { AdminLeadsPanel } from "@/components/admin/AdminLeadsPanel";
import { AdminUtmBuilder } from "@/components/admin/AdminUtmBuilder";

const ADMIN_LANGUAGE_STORAGE_KEY = "trinity_admin_editor_language";

const editorTabs = [
  { id: "leads", label: "Leads" },
  { id: "utm", label: "UTM Builder" },
  { id: "hero", label: "Hero" },
  { id: "snapshot", label: "Snapshot" },
  { id: "ecosystem", label: "Ecosystem" },
  { id: "milestones", label: "Milestones" },
  { id: "revenue", label: "Revenue" },
  { id: "model", label: "What Trinity Does" },
  { id: "capabilities", label: "Core Competencies" },
  { id: "solutions", label: "Business Domains" },
  { id: "proof", label: "Proof" },
  { id: "global", label: "Global" },
  { id: "phison", label: "Phison Pitch" },
  { id: "team", label: "Representatives" },
  { id: "sections", label: "Section Sequence" },
  { id: "partnership", label: "Partnership" },
  { id: "media", label: "Media & Pins" },
];

const sectionLabels = {
  hero: "Hero",
  snapshot: "Company Snapshot",
  ecosystem: "Brand & Technology Ecosystem",
  milestones: "Milestones",
  revenue: "Revenue Growth",
  trinityModel: "What Trinity Actually Does",
  proof: "Project Proof Areas",
  capabilities: "Core Competencies",
  solutions: "Business Domains",
  global: "Global Presence",
  phison: "Phison Partnership Brief",
  team: "Representatives",
  partnershipModels: "Ways to Work With Trinity",
  partnership: "Partnership Opportunities",
};

const contactFields = ["email", "whatsapp", "linkedin", "telegram", "vk"];

const mediaSectionLabels = {
  snapshot: "Company Snapshot Card",
  ecosystem: "Ecosystem Brand",
  milestones: "Milestone Card",
  trinityModel: "What Trinity Does Card",
  capabilities: "Core Competency Card",
  solutions: "Business Domain Card",
  phison: "Phison Brief Card",
  partnershipModels: "Partnership Model Card",
};

const languageLabels = {
  en: "English",
  "zh-TW": "Traditional Chinese (Taiwan)",
  es: "Spanish",
  ru: "Russian",
  ar: "Arabic",
};

const imageUrlPlaceholder = "Paste image URL, e.g. https://example.com/trinity-product-photo.jpg";

const cloneContent = (value) => JSON.parse(JSON.stringify(value));

const cloneSeed = () => cloneContent(getEditableSeedContent());

const updateAtPath = (source, path, value) => {
  const next = cloneContent(source || cloneSeed());
  let cursor = next;
  path.slice(0, -1).forEach((key, index) => {
    const nextKey = path[index + 1];
    if (cursor[key] === undefined || cursor[key] === null) cursor[key] = typeof nextKey === "number" ? [] : {};
    cursor = cursor[key];
  });
  cursor[path[path.length - 1]] = value;
  return next;
};

const asCoordinateText = (coordinates = []) => coordinates.join(", ");

const parseCoordinateText = (text) => text.split(",").map((item) => Number(item.trim())).filter((item) => !Number.isNaN(item));

const moveItem = (items, index, direction) => {
  const next = [...items];
  const target = index + direction;
  if (target < 0 || target >= next.length) return next;
  [next[index], next[target]] = [next[target], next[index]];
  return next;
};

export default function AdminEditor() {
  const [email, setEmail] = useState("admin@trinity.local");
  const [password, setPassword] = useState("");
  const [token, setToken] = useState(sessionStorage.getItem("trinity_admin_token") || "");
  const [content, setContent] = useState(cloneSeed());
  const [message, setMessage] = useState("");
  const [language, setLanguage] = useState(() => getStoredLanguage(ADMIN_LANGUAGE_STORAGE_KEY));
  const [activeTab, setActiveTab] = useState("hero");
  const [editorResetVersion, setEditorResetVersion] = useState(0);

  const handleLanguageChange = useCallback((nextLanguage) => {
    if (!isSupportedLanguage(nextLanguage)) return;
    storeLanguage(ADMIN_LANGUAGE_STORAGE_KEY, nextLanguage);
    setLanguage(nextLanguage);
  }, []);

  const clearAdminSession = useCallback(() => {
    sessionStorage.removeItem("trinity_admin_token");
    setToken("");
  }, []);

  useEffect(() => {
    if (!token) return;
    fetchAdminContent(token)
      .then((content) => {
        const editable = Object.keys(content || {}).length ? normalizeEditableContent(content) : cloneSeed();
        setContent(editable);
      })
      .catch((error) => {
        if (error.response?.status === 401) {
          clearAdminSession();
          setMessage("Your admin session expired. Please login again before saving.");
          return;
        }
        setMessage("Could not load saved content. Please login again.");
      });
  }, [token, clearAdminSession]);

  const current = useMemo(() => content.translations?.[language] || content.translations?.en || cloneSeed().translations.en, [content, language]);

  const updatePath = useCallback((path, value) => setContent((previous) => updateAtPath(previous, path, value)), []);

  const updateTranslation = useCallback((path, value) => updatePath(["translations", language, ...path], value), [language, updatePath]);

  const handleLogin = async (event) => {
    event.preventDefault();
    setMessage("Logging in...");
    try {
      const data = await loginAdmin(email, password);
      sessionStorage.setItem("trinity_admin_token", data.token);
      setToken(data.token);
      setMessage("Logged in. You can now edit content with forms.");
    } catch (error) {
      setMessage(error.response?.data?.detail || "Login failed");
    }
  };

  const handleSave = async () => {
    setMessage("Saving...");
    try {
      const saved = await saveAdminContent(token, content);
      setContent(normalizeEditableContent(saved.content || content));
      storeLanguage(ADMIN_LANGUAGE_STORAGE_KEY, language);
      setMessage(`Saved ${languageLabels[language] || language.toUpperCase()} successfully at ${new Date().toLocaleTimeString()}. Public page will use this content.`);
    } catch (error) {
      if (error.response?.status === 401) {
        clearAdminSession();
        setMessage("Your admin session expired. Please login again, then click Save.");
        return;
      }
      setMessage(error.message || "Save failed");
    }
  };

  const handleResetSeed = () => {
    setContent(normalizeEditableContent(cloneSeed()));
    setEditorResetVersion((version) => version + 1);
    setMessage("Loaded editable seed content. Save to publish it.");
  };

  const renderHero = () => (
    <AdminSection title="Hero" eyebrow="Homepage first section">
      <AdminField id="admin-hero-overline" label="Overline" value={current.hero?.overline} onChange={(value) => updateTranslation(["hero", "overline"], value)} />
      <AdminField id="admin-hero-company" label="Company Label" value={current.hero?.company} onChange={(value) => updateTranslation(["hero", "company"], value)} />
      <AdminField id="admin-hero-headline" label="Headline" value={current.hero?.headline} onChange={(value) => updateTranslation(["hero", "headline"], value)} />
      <AdminField id="admin-hero-subheadline" label="Subheadline" value={current.hero?.subheadline} onChange={(value) => updateTranslation(["hero", "subheadline"], value)} />
      <AdminField id="admin-hero-description" label="Description" value={current.hero?.description} multiline onChange={(value) => updateTranslation(["hero", "description"], value)} />
      <AdminListField id="admin-hero-domains" label="Domain Tags" values={current.hero?.domains} onChange={(value) => updateTranslation(["hero", "domains"], value)} />
      <AdminListField id="admin-hero-panel-labels" label="Image Panel Labels" values={current.hero?.panelLabels} onChange={(value) => updateTranslation(["hero", "panelLabels"], value)} />
      <AdminListField id="admin-hero-buttons" label="CTA Buttons" values={current.hero?.ctas} onChange={(value) => updateTranslation(["hero", "ctas"], value)} />
    </AdminSection>
  );

  const renderSnapshot = () => (
    <AdminSection title="Snapshot" eyebrow="Company facts">
      <AdminField id="admin-snapshot-overline" label="Overline" value={current.snapshot?.overline} onChange={(value) => updateTranslation(["snapshot", "overline"], value)} />
      <AdminField id="admin-snapshot-title" label="Title" value={current.snapshot?.title} onChange={(value) => updateTranslation(["snapshot", "title"], value)} />
      <AdminField id="admin-snapshot-description" label="Description" value={current.snapshot?.description} multiline onChange={(value) => updateTranslation(["snapshot", "description"], value)} />
      {(current.snapshot?.facts || []).map((fact, index) => (
        <div className="admin-nested-card" key={`${fact.label}-${index}`} data-testid={`admin-snapshot-fact-${index}-card`}>
          <AdminField id={`admin-snapshot-fact-${index}-label`} label={`Fact ${index + 1} Label`} value={fact.label} onChange={(value) => updateTranslation(["snapshot", "facts", index, "label"], value)} />
          <AdminField id={`admin-snapshot-fact-${index}-value`} label={`Fact ${index + 1} Value`} value={fact.value} onChange={(value) => updateTranslation(["snapshot", "facts", index, "value"], value)} />
        </div>
      ))}
    </AdminSection>
  );

  const renderMilestones = () => (
    <AdminSection title="Milestones" eyebrow="Company timeline">
      <AdminField id="admin-milestones-overline" label="Overline" value={current.milestones?.overline} onChange={(value) => updateTranslation(["milestones", "overline"], value)} />
      <AdminField id="admin-milestones-title" label="Title" value={current.milestones?.title} onChange={(value) => updateTranslation(["milestones", "title"], value)} />
      <AdminField id="admin-milestones-description" label="Description" value={current.milestones?.description} multiline onChange={(value) => updateTranslation(["milestones", "description"], value)} />
      {(current.milestones?.items || []).map((item, index) => (
        <div className="admin-nested-card wide" key={`${item.year}-${index}`} data-testid={`admin-milestone-${index}-editor`}>
          <AdminField id={`admin-milestone-${index}-year`} label={`Milestone ${index + 1} Year`} value={item.year} onChange={(value) => updateTranslation(["milestones", "items", index, "year"], value)} />
          <AdminField id={`admin-milestone-${index}-title`} label="Title" value={item.title} onChange={(value) => updateTranslation(["milestones", "items", index, "title"], value)} />
          <AdminField id={`admin-milestone-${index}-text`} label="Text" value={item.text} multiline onChange={(value) => updateTranslation(["milestones", "items", index, "text"], value)} />
        </div>
      ))}
    </AdminSection>
  );

  const renderEcosystem = () => (
    <AdminSection title="Brand & Technology Ecosystem" eyebrow="Brands and technology relationships">
      <AdminField id="admin-ecosystem-overline" label="Overline" value={current.ecosystem?.overline} onChange={(value) => updateTranslation(["ecosystem", "overline"], value)} />
      <AdminField id="admin-ecosystem-title" label="Title" value={current.ecosystem?.title} onChange={(value) => updateTranslation(["ecosystem", "title"], value)} />
      <AdminField id="admin-ecosystem-description" label="Description" value={current.ecosystem?.description} multiline onChange={(value) => updateTranslation(["ecosystem", "description"], value)} />
      {(current.ecosystem?.items || []).map((item, index) => (
        <div className="admin-nested-card wide" key={`${item.name}-${index}`} data-testid={`admin-ecosystem-item-${index}-editor`}>
          <AdminField id={`admin-ecosystem-item-${index}-name`} label={`Entity ${index + 1} Name`} value={item.name} onChange={(value) => updateTranslation(["ecosystem", "items", index, "name"], value)} />
          <AdminField id={`admin-ecosystem-item-${index}-relationship`} label="Relationship / Role" value={item.relationship} onChange={(value) => updateTranslation(["ecosystem", "items", index, "relationship"], value)} />
          <AdminField id={`admin-ecosystem-item-${index}-description`} label="Description" value={item.description} multiline onChange={(value) => updateTranslation(["ecosystem", "items", index, "description"], value)} />
          <AdminField id={`admin-ecosystem-item-${index}-image`} label="Logo / Image URL" value={content.common?.sectionMedia?.ecosystem?.[index]?.image} placeholder={imageUrlPlaceholder} onChange={(value) => updatePath(["common", "sectionMedia", "ecosystem", index, "image"], value)} />
        </div>
      ))}
    </AdminSection>
  );

  const renderProof = () => (
    <AdminSection title="Proof" eyebrow="Project proof areas">
      <AdminField id="admin-proof-overline" label="Overline" value={current.proof?.overline} onChange={(value) => updateTranslation(["proof", "overline"], value)} />
      <AdminField id="admin-proof-title" label="Title" value={current.proof?.title} onChange={(value) => updateTranslation(["proof", "title"], value)} />
      <AdminField id="admin-proof-description" label="Description" value={current.proof?.description} multiline onChange={(value) => updateTranslation(["proof", "description"], value)} />
      <AdminField id="admin-proof-logo-strip-eyebrow" label="Client Logo Section Tagline" value={current.proof?.logoStrip?.eyebrow} placeholder="e.g. Selected Client References" onChange={(value) => updateTranslation(["proof", "logoStrip", "eyebrow"], value)} />
      <AdminField id="admin-proof-logo-strip-title" label="Client Logo Section Title" value={current.proof?.logoStrip?.title} placeholder="e.g. Client and partner logo placeholders" onChange={(value) => updateTranslation(["proof", "logoStrip", "title"], value)} />
      {(current.proof?.stats || []).map((stat, index) => (
        <div className="admin-nested-card compact" key={`${stat.value}-${index}`} data-testid={`admin-proof-stat-${index}-card`}>
          <AdminField id={`admin-proof-stat-${index}-value`} label={`Stat ${index + 1} Value`} value={stat.value} onChange={(value) => updateTranslation(["proof", "stats", index, "value"], value)} />
          <AdminField id={`admin-proof-stat-${index}-label`} label={`Stat ${index + 1} Label`} value={stat.label} onChange={(value) => updateTranslation(["proof", "stats", index, "label"], value)} />
        </div>
      ))}
      {(current.proof?.cards || []).map((card, index) => (
        <div className="admin-nested-card wide" key={`${card.title}-${index}`} data-testid={`admin-proof-card-${index}-editor`}>
          <AdminField id={`admin-proof-card-${index}-title`} label={`Proof Card ${index + 1} Title`} value={card.title} onChange={(value) => updateTranslation(["proof", "cards", index, "title"], value)} />
          <AdminField id={`admin-proof-card-${index}-category`} label="Category" value={card.category} onChange={(value) => updateTranslation(["proof", "cards", index, "category"], value)} />
          <AdminField id={`admin-proof-card-${index}-image`} label="Case Study Image URL" value={content.common?.proofImages?.[index]?.image} placeholder={imageUrlPlaceholder} onChange={(value) => updatePath(["common", "proofImages", index, "image"], value)} />
          <AdminListField id={`admin-proof-card-${index}-scope`} label="Scope Items" values={card.scope} onChange={(value) => updateTranslation(["proof", "cards", index, "scope"], value)} />
          <AdminField id={`admin-proof-card-${index}-outcome`} label="Outcome" value={card.outcome} multiline onChange={(value) => updateTranslation(["proof", "cards", index, "outcome"], value)} />
          <AdminField id={`admin-proof-card-${index}-link-label`} label="External Link Label" value={card.linkLabel} placeholder="e.g. View product specifications" onChange={(value) => updateTranslation(["proof", "cards", index, "linkLabel"], value)} />
          <AdminField id={`admin-proof-card-${index}-link`} label="External Link URL" value={card.link} placeholder="https://example.com/product" onChange={(value) => updateTranslation(["proof", "cards", index, "link"], value)} />
        </div>
      ))}
    </AdminSection>
  );

  const renderRevenue = () => (
    <AdminSection title="Revenue" eyebrow="Commercial growth">
      <AdminField id="admin-revenue-overline" label="Overline" value={current.revenue?.overline} onChange={(value) => updateTranslation(["revenue", "overline"], value)} />
      <AdminField id="admin-revenue-title" label="Title" value={current.revenue?.title} onChange={(value) => updateTranslation(["revenue", "title"], value)} />
      <AdminField id="admin-revenue-description" label="Description" value={current.revenue?.description} multiline onChange={(value) => updateTranslation(["revenue", "description"], value)} />
      {(current.revenue?.items || []).map((item, index) => (
        <div className="admin-nested-card" key={`${item.year}-${index}`} data-testid={`admin-revenue-item-${index}-editor`}>
          <AdminField id={`admin-revenue-item-${index}-year`} label={`Revenue ${index + 1} Year`} value={item.year} onChange={(value) => updateTranslation(["revenue", "items", index, "year"], value)} />
          <AdminField id={`admin-revenue-item-${index}-value`} label="Value" value={item.value} onChange={(value) => updateTranslation(["revenue", "items", index, "value"], value)} />
          <AdminField id={`admin-revenue-item-${index}-label`} label="Label" value={item.label} onChange={(value) => updateTranslation(["revenue", "items", index, "label"], value)} />
        </div>
      ))}
    </AdminSection>
  );

  const renderModel = () => (
    <AdminSection title="What Trinity Does" eyebrow="Operating model and positioning">
      <AdminField id="admin-model-overline" label="Overline" value={current.operatingModel?.overline} onChange={(value) => updateTranslation(["operatingModel", "overline"], value)} />
      <AdminField id="admin-model-title" label="Title" value={current.operatingModel?.title} onChange={(value) => updateTranslation(["operatingModel", "title"], value)} />
      <AdminField id="admin-model-description" label="Description" value={current.operatingModel?.description} multiline onChange={(value) => updateTranslation(["operatingModel", "description"], value)} />
      {(current.operatingModel?.cards || []).map((card, index) => (
        <div className="admin-nested-card wide" key={`${card.title}-${index}`} data-testid={`admin-model-card-${index}-editor`}>
          <AdminField id={`admin-model-card-${index}-title`} label={`Model Card ${index + 1} Title`} value={card.title} onChange={(value) => updateTranslation(["operatingModel", "cards", index, "title"], value)} />
          <AdminField id={`admin-model-card-${index}-text`} label="Text" value={card.text} multiline onChange={(value) => updateTranslation(["operatingModel", "cards", index, "text"], value)} />
          <AdminListField id={`admin-model-card-${index}-items`} label="Items" values={card.items} onChange={(value) => updateTranslation(["operatingModel", "cards", index, "items"], value)} />
        </div>
      ))}
      {(current.positioning?.cards || []).map(([title, text], index) => (
        <div className="admin-nested-card" key={`${title}-${index}`} data-testid={`admin-positioning-card-${index}-editor`}>
          <AdminField id={`admin-positioning-card-${index}-title`} label={`Positioning ${index + 1} Title`} value={title} onChange={(value) => updateTranslation(["positioning", "cards", index, 0], value)} />
          <AdminField id={`admin-positioning-card-${index}-text`} label="Text" value={text} multiline onChange={(value) => updateTranslation(["positioning", "cards", index, 1], value)} />
        </div>
      ))}
    </AdminSection>
  );

  const renderCapabilities = () => (
    <AdminSection title="Core Competencies" eyebrow="Technical and commercial capabilities">
      <AdminField id="admin-capabilities-overline" label="Overline" value={current.capabilities?.overline} onChange={(value) => updateTranslation(["capabilities", "overline"], value)} />
      <AdminField id="admin-capabilities-title" label="Title" value={current.capabilities?.title} onChange={(value) => updateTranslation(["capabilities", "title"], value)} />
      <AdminField id="admin-camera-description" label="Section Description" value={current.cameraExpertise?.description} multiline onChange={(value) => updateTranslation(["cameraExpertise", "description"], value)} />
      {(current.capabilities?.columns || []).map((column, index) => (
        <div className="admin-nested-card" key={`${column.title}-${index}`} data-testid={`admin-capability-column-${index}-editor`}>
          <AdminField id={`admin-capability-column-${index}-title`} label={`Column ${index + 1} Title`} value={column.title} onChange={(value) => updateTranslation(["capabilities", "columns", index, "title"], value)} />
          <AdminListField id={`admin-capability-column-${index}-items`} label="Items" values={column.items} onChange={(value) => updateTranslation(["capabilities", "columns", index, "items"], value)} />
        </div>
      ))}
      <AdminField id="admin-camera-activity-number" label="Camera Activity Number" value={current.cameraExpertise?.activity?.number} onChange={(value) => updateTranslation(["cameraExpertise", "activity", "number"], value)} />
      <AdminField id="admin-camera-activity-title" label="Camera Activity Title" value={current.cameraExpertise?.activity?.title} onChange={(value) => updateTranslation(["cameraExpertise", "activity", "title"], value)} />
      <AdminField id="admin-camera-iq-text" label="Camera Activity Text" value={current.cameraExpertise?.iq?.text} multiline onChange={(value) => updateTranslation(["cameraExpertise", "iq", "text"], value)} />
    </AdminSection>
  );

  const renderSolutions = () => (
    <AdminSection title="Business Domains" eyebrow="Solution cards">
      <AdminField id="admin-solutions-overline" label="Overline" value={current.solutions?.overline} onChange={(value) => updateTranslation(["solutions", "overline"], value)} />
      <AdminField id="admin-solutions-title" label="Title" value={current.solutions?.title} onChange={(value) => updateTranslation(["solutions", "title"], value)} />
      {(current.solutions?.domains || []).map((domain, index) => (
        <div className="admin-nested-card wide" key={`${domain.title}-${index}`} data-testid={`admin-solution-domain-${index}-editor`}>
          <AdminField id={`admin-solution-domain-${index}-title`} label={`Domain ${index + 1} Title`} value={domain.title} onChange={(value) => updateTranslation(["solutions", "domains", index, "title"], value)} />
          <AdminField id={`admin-solution-domain-${index}-eyebrow`} label="Eyebrow" value={domain.eyebrow} onChange={(value) => updateTranslation(["solutions", "domains", index, "eyebrow"], value)} />
          <AdminField id={`admin-solution-domain-${index}-description`} label="Description" value={domain.description} multiline onChange={(value) => updateTranslation(["solutions", "domains", index, "description"], value)} />
          <AdminListField id={`admin-solution-domain-${index}-items`} label="Feature Items" values={domain.items} onChange={(value) => updateTranslation(["solutions", "domains", index, "items"], value)} />
          <AdminListField id={`admin-solution-domain-${index}-products`} label="Product Tags" values={domain.products} onChange={(value) => updateTranslation(["solutions", "domains", index, "products"], value)} />
        </div>
      ))}
    </AdminSection>
  );

  const renderGlobal = () => (
    <AdminSection title="Global" eyebrow="Markets and map text">
      <AdminField id="admin-global-overline" label="Overline" value={current.global?.overline} onChange={(value) => updateTranslation(["global", "overline"], value)} />
      <AdminField id="admin-global-title" label="Title" value={current.global?.title} onChange={(value) => updateTranslation(["global", "title"], value)} />
      <AdminListField id="admin-global-regions" label="Region Labels" values={current.global?.regions} onChange={(value) => updateTranslation(["global", "regions"], value)} />
      <AdminListField id="admin-global-stats" label="Global Stats" values={current.global?.stats} onChange={(value) => updateTranslation(["global", "stats"], value)} />
    </AdminSection>
  );

  const renderTeam = () => (
    <AdminSection title="Representatives" eyebrow="Regional contacts">
      <AdminField id="admin-team-overline" label="Overline" value={current.team?.overline} onChange={(value) => updateTranslation(["team", "overline"], value)} />
      <AdminField id="admin-team-title" label="Title" value={current.team?.title} onChange={(value) => updateTranslation(["team", "title"], value)} />
      <AdminField id="admin-team-photo-label" label="Photo Placeholder Label" value={current.team?.photo} onChange={(value) => updateTranslation(["team", "photo"], value)} />
      <p className="admin-note" data-testid="admin-team-name-sharing-note">Representative names and images are shared across all languages. Region, role, coverage, and contact fields remain language-specific.</p>
      {(current.team?.teams || []).map((team, index) => {
        const [region, representative, coverage] = team;
        const contacts = team[3] || {};
        return (
          <div className="admin-nested-card wide" key={`${region}-${index}`} data-testid={`admin-team-representative-${index}-editor`}>
            <AdminField id={`admin-team-${index}-region`} label={`Representative ${index + 1} Region`} value={region} onChange={(value) => updateTranslation(["team", "teams", index, 0], value)} />
            <AdminField id={`admin-team-${index}-name`} label="Representative Name" value={content.common?.teamImages?.[index]?.name} placeholder="e.g. Maria Chen" onChange={(value) => updatePath(["common", "teamImages", index, "name"], value)} />
            <AdminField id={`admin-team-${index}-role`} label="Representative Role / Label" value={representative} onChange={(value) => updateTranslation(["team", "teams", index, 1], value)} />
            <AdminListField id={`admin-team-${index}-coverage`} label="Coverage Areas" values={coverage} onChange={(value) => updateTranslation(["team", "teams", index, 2], value)} />
            <AdminField id={`admin-team-${index}-image`} label="Representative Image URL" value={content.common?.teamImages?.[index]?.image} placeholder={imageUrlPlaceholder} onChange={(value) => updatePath(["common", "teamImages", index, "image"], value)} />
            {contactFields.map((field) => (
              <AdminField key={field} id={`admin-team-${index}-${field}`} label={`${field.charAt(0).toUpperCase()}${field.slice(1)} Link / Value`} value={contacts[field]} onChange={(value) => updateTranslation(["team", "teams", index, 3, field], value)} />
            ))}
          </div>
        );
      })}
    </AdminSection>
  );

  const renderPhison = () => (
    <AdminSection title="Phison Pitch" eyebrow="Partner-specific section">
      <AdminField id="admin-phison-overline" label="Overline" value={current.phison?.overline} onChange={(value) => updateTranslation(["phison", "overline"], value)} />
      <AdminField id="admin-phison-title" label="Title" value={current.phison?.title} onChange={(value) => updateTranslation(["phison", "title"], value)} />
      <AdminField id="admin-phison-description" label="Description" value={current.phison?.description} multiline onChange={(value) => updateTranslation(["phison", "description"], value)} />
      {(current.phison?.points || []).map((point, index) => (
        <div className="admin-nested-card" key={`${point.title}-${index}`} data-testid={`admin-phison-point-${index}-editor`}>
          <AdminField id={`admin-phison-point-${index}-title`} label={`Point ${index + 1} Title`} value={point.title} onChange={(value) => updateTranslation(["phison", "points", index, "title"], value)} />
          <AdminField id={`admin-phison-point-${index}-text`} label="Text" value={point.text} multiline onChange={(value) => updateTranslation(["phison", "points", index, "text"], value)} />
        </div>
      ))}
      <AdminListField id="admin-phison-stats" label="Stat Chips" values={current.phison?.stats} onChange={(value) => updateTranslation(["phison", "stats"], value)} />
    </AdminSection>
  );

  const renderPartnership = () => (
    <AdminSection title="Partnership" eyebrow="Final call to action">
      <AdminField id="admin-partnership-overline" label="Overline" value={current.partnership?.overline} onChange={(value) => updateTranslation(["partnership", "overline"], value)} />
      <AdminField id="admin-partnership-title" label="Title" value={current.partnership?.title} onChange={(value) => updateTranslation(["partnership", "title"], value)} />
      <AdminField id="admin-partnership-description" label="Description" value={current.partnership?.description} multiline onChange={(value) => updateTranslation(["partnership", "description"], value)} />
      <AdminListField id="admin-partnership-buttons" label="CTA Buttons" values={current.partnership?.buttons} onChange={(value) => updateTranslation(["partnership", "buttons"], value)} />
      <p className="admin-note" data-testid="admin-partnership-note">Partner chips were removed from the public page.</p>
    </AdminSection>
  );

  const renderMedia = () => (
    <AdminSection title="Media & Pins" eyebrow="Images and map coordinates">
      <p className="admin-note" data-testid="admin-client-logo-help-note">Client logo images appear below Project Proof Areas. You can use https image URLs, domain-style URLs, or data image URLs. The section title/tagline are edited in the Proof Areas tab.</p>
      <AdminField id="admin-media-logo-strip-eyebrow" label="Client Logo Section Tagline" value={current.proof?.logoStrip?.eyebrow} placeholder="e.g. Selected Client References" onChange={(value) => updateTranslation(["proof", "logoStrip", "eyebrow"], value)} />
      <AdminField id="admin-media-logo-strip-title" label="Client Logo Section Title" value={current.proof?.logoStrip?.title} placeholder="e.g. Client and partner logo placeholders" onChange={(value) => updateTranslation(["proof", "logoStrip", "title"], value)} />
      {(content.common?.heroPanels || []).map((panel, index) => (
        <AdminField key={`panel-${index}`} id={`admin-hero-panel-${index}-image`} label={`Hero Panel ${index + 1} Image URL`} value={panel.image} placeholder={imageUrlPlaceholder} onChange={(value) => updatePath(["common", "heroPanels", index, "image"], value)} />
      ))}
      {(content.common?.proofImages || []).map((image, index) => (
        <AdminField key={`proof-image-${index}`} id={`admin-proof-media-${index}-image`} label={`Project Proof Area ${index + 1} Image URL`} value={image.image} placeholder={imageUrlPlaceholder} onChange={(value) => updatePath(["common", "proofImages", index, "image"], value)} />
      ))}
      {(content.common?.teamImages || []).map((image, index) => (
        <AdminField key={`team-image-${index}`} id={`admin-team-media-${index}-image`} label={`Representative ${index + 1} Image URL`} value={image.image} placeholder={imageUrlPlaceholder} onChange={(value) => updatePath(["common", "teamImages", index, "image"], value)} />
      ))}
      {(content.common?.clientLogos || []).map((logo, index) => (
        <div className="admin-nested-card" key={`client-logo-${index}`} data-testid={`admin-client-logo-${index}-card`}>
          <AdminField id={`admin-client-logo-${index}-name`} label={`Client Logo ${index + 1} Name`} value={logo.name} placeholder={`Client ${index + 1}`} onChange={(value) => updatePath(["common", "clientLogos", index, "name"], value)} />
          <AdminField id={`admin-client-logo-${index}-image`} label={`Client Logo ${index + 1} Image URL`} value={logo.image} placeholder={imageUrlPlaceholder} onChange={(value) => updatePath(["common", "clientLogos", index, "image"], value)} />
        </div>
      ))}
      {Object.entries(content.common?.sectionMedia || {}).map(([sectionKey, slots]) => (
        <div className="admin-nested-card wide" key={`section-media-${sectionKey}`} data-testid={`admin-section-media-${sectionKey}-group`}>
          <p className="admin-note" data-testid={`admin-section-media-${sectionKey}-note`}>{sectionLabels[sectionKey] || sectionKey} additional card photos</p>
          {(slots || []).map((slot, index) => (
            <AdminField
              key={`${sectionKey}-media-${index}`}
              id={`admin-section-media-${sectionKey}-${index}-image`}
              label={`${mediaSectionLabels[sectionKey] || sectionKey} ${index + 1} Image URL`}
              value={slot.image}
              placeholder={imageUrlPlaceholder}
              onChange={(value) => updatePath(["common", "sectionMedia", sectionKey, index, "image"], value)}
            />
          ))}
        </div>
      ))}
      {(content.common?.mapRegions || []).map((region, index) => (
        <div className="admin-nested-card compact" key={region.key} data-testid={`admin-map-region-${region.key}-card`}>
          <AdminField id={`admin-map-region-${index}-key`} label="Region Key" value={region.key} onChange={(value) => updatePath(["common", "mapRegions", index, "key"], value)} />
          <AdminField id={`admin-map-region-${index}-coordinates`} label="Coordinates: longitude, latitude" value={asCoordinateText(region.coordinates)} onChange={(value) => updatePath(["common", "mapRegions", index, "coordinates"], parseCoordinateText(value))} />
          <AdminField id={`admin-map-region-${index}-group`} label="Region Group" value={region.group} onChange={(value) => updatePath(["common", "mapRegions", index, "group"], value)} />
        </div>
      ))}
    </AdminSection>
  );

  const renderSections = () => (
    <AdminSection title="Section Sequence" eyebrow="Language-specific homepage order controls">
      <p className="admin-note" data-testid="admin-section-hide-note">Use Hide to remove a section from the selected language only. Order and visibility no longer affect other languages.</p>
      <div className="admin-sequence-list" data-testid="admin-section-sequence-list">
        {(current.sectionOrder || content.common?.sectionOrder || []).map((sectionKey, index) => (
          <div className="admin-sequence-item" key={sectionKey} data-testid={`admin-section-sequence-${sectionKey}`}>
            <span data-testid={`admin-section-sequence-label-${sectionKey}`}>{String(index + 1).padStart(2, "0")} · {sectionLabels[sectionKey] || sectionKey}</span>
            <div className="admin-sequence-actions" data-testid={`admin-section-sequence-actions-${sectionKey}`}>
              <button
                type="button"
                className={(current.hiddenSections || []).includes(sectionKey) ? "active" : ""}
                onClick={() => {
                  const hidden = current.hiddenSections || [];
                  updateTranslation(["hiddenSections"], hidden.includes(sectionKey) ? hidden.filter((item) => item !== sectionKey) : [...hidden, sectionKey]);
                }}
                data-testid={`admin-section-${sectionKey}-hide-toggle-button`}
              >
                {(current.hiddenSections || []).includes(sectionKey) ? "Show" : "Hide"}
              </button>
              <button type="button" onClick={() => updateTranslation(["sectionOrder"], moveItem(current.sectionOrder || content.common.sectionOrder, index, -1))} data-testid={`admin-section-${sectionKey}-move-up-button`}>Up</button>
              <button type="button" onClick={() => updateTranslation(["sectionOrder"], moveItem(current.sectionOrder || content.common.sectionOrder, index, 1))} data-testid={`admin-section-${sectionKey}-move-down-button`}>Down</button>
            </div>
          </div>
        ))}
      </div>
    </AdminSection>
  );

  const renderActiveTab = () => {
    if (activeTab === "leads") return <AdminLeadsPanel token={token} onUnauthorized={clearAdminSession} />;
    if (activeTab === "utm") return <AdminUtmBuilder token={token} onUnauthorized={clearAdminSession} />;
    if (activeTab === "snapshot") return renderSnapshot();
    if (activeTab === "ecosystem") return renderEcosystem();
    if (activeTab === "milestones") return renderMilestones();
    if (activeTab === "revenue") return renderRevenue();
    if (activeTab === "model") return renderModel();
    if (activeTab === "capabilities") return renderCapabilities();
    if (activeTab === "solutions") return renderSolutions();
    if (activeTab === "proof") return renderProof();
    if (activeTab === "global") return renderGlobal();
    if (activeTab === "team") return renderTeam();
    if (activeTab === "phison") return renderPhison();
    if (activeTab === "sections") return renderSections();
    if (activeTab === "partnership") return renderPartnership();
    if (activeTab === "media") return renderMedia();
    return renderHero();
  };

  if (!token) {
    return (
      <main className="admin-page" data-testid="admin-login-page">
        <form className="admin-card" onSubmit={handleLogin} data-testid="admin-login-form">
          <p className="overline" data-testid="admin-login-overline">Trinity Content Editor</p>
          <h1 data-testid="admin-login-title">Admin Login</h1>
          <label data-testid="admin-email-label">Email</label>
          <input value={email} onChange={(event) => setEmail(event.target.value)} data-testid="admin-email-input" />
          <label data-testid="admin-password-label">Password</label>
          <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} data-testid="admin-password-input" />
          <button type="submit" data-testid="admin-login-button">Login</button>
          <p data-testid="admin-login-message">{message}</p>
        </form>
      </main>
    );
  }

  return (
    <main className="admin-page editor" data-testid="admin-editor-page">
      <div className="admin-toolbar" data-testid="admin-toolbar">
        <div>
          <p className="overline" data-testid="admin-editor-overline">Trinity Content Editor</p>
          <h1 data-testid="admin-editor-title">Form-Based Content Editor</h1>
        </div>
        <div className="admin-actions">
          {!['leads', 'utm'].includes(activeTab) && <button type="button" onClick={handleResetSeed} data-testid="admin-load-seed-button">Load Seed</button>}
          {!['leads', 'utm'].includes(activeTab) && <button type="button" onClick={handleSave} data-testid="admin-save-button">Save</button>}
          <button type="button" onClick={clearAdminSession} data-testid="admin-logout-button">Logout</button>
        </div>
      </div>
      <p className="admin-help" data-testid="admin-help-text">
        Select a language, edit structured fields, then save. Text, section sequence, section visibility, and representative contacts affect only the selected language; shared images and map coordinates affect all languages.
      </p>
      <p className="admin-language-scope" data-testid="admin-language-scope-indicator">
        Editing text for {languageLabels[language] || language.toUpperCase()} only
      </p>
      {message && (
        <p className="admin-status" role="status" data-testid="admin-editor-message">{message}</p>
      )}
      <div className="admin-editor-shell" data-testid="admin-editor-shell">
        <aside className="admin-sidebar" data-testid="admin-sidebar">
          <div className="admin-language-panel" data-testid="admin-language-panel">
            {Object.keys(content.translations || {}).map((code) => (
              <button
                type="button"
                key={code}
                className={language === code ? "active" : ""}
                onClick={() => handleLanguageChange(code)}
                data-testid={`admin-language-${code}-button`}
              >
                {code.toUpperCase()}
              </button>
            ))}
          </div>
          <nav className="admin-tab-list" data-testid="admin-tab-list">
            {editorTabs.map((tab) => (
              <button
                type="button"
                key={tab.id}
                className={activeTab === tab.id ? "active" : ""}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`admin-tab-${tab.id}-button`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </aside>
        <div className="admin-form-area" data-testid="admin-form-area" key={`editor-${editorResetVersion}`}>
          {renderActiveTab()}
        </div>
      </div>
    </main>
  );
}